import { Component, inject, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { Auth } from '@core/services';
import { Title, Toaster } from '@shared/services';
import { DateTimePicker } from '@shared/date-time-picker/date-time-picker';
import { Campaign as CampaignService } from '@modules/campaign/services/campaign';

@Component({
  selector: 'app-campaign',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DateTimePicker],
  templateUrl: './campaign.html',
  styleUrl: './campaign.scss'
})
export class Campaign implements OnInit {
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private authService = inject(Auth);
  private titleService = inject(Title);
  private toasterService = inject(Toaster);
  private campaignService = inject(CampaignService);

  campaignType = signal<'unicast' | 'multicast' | 'excel'>('unicast');
  selectedFile = signal<File | null>(null);
  isValidated = signal(false);
  isValidating = signal(false);
  campaignCost = signal(0);
  totalRecipients = signal(0);
  packages = signal<any[]>([]);
  charCount = 0;
  messageCount = 0;

  campaignForm: FormGroup = this.fb.group({
    name: ['', [Validators.required]],
    package: ['', [Validators.required]],
    senderId: ['', [Validators.required]],
    messageText: ['', [Validators.required, Validators.maxLength(1000)]],
    scheduleDate: ['']
  });

  user = computed(() => this.authService.user || {});
  senderIds = computed(() => this.user().senderId);

  ngOnInit() {
    this.titleService.setTitle('Campaign');
    this.getPackages();

    this.campaignForm.get('messageText')?.valueChanges.subscribe((value) => {
      this.updateCounts(value || '');
      this.resetValidation();
    });

    // Reset validation when any field changes
    this.campaignForm.valueChanges.subscribe(() => {
      if (!this.isValidating()) {
        this.resetValidation();
      }
    });

    this.campaignForm.patchValue({ senderId: this.senderIds()[0] });
  }

  getPackages() {
    this.campaignService.packageList({}).subscribe({
      next: (response: { data: any }) => {
        this.packages.set(response.data);
        this.campaignForm.patchValue({ package: this.packages()[0] });
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
      }
    });
  }

  updateCounts(text: string) {
    this.charCount = text.length;
    this.messageCount = text.length > 0 ? Math.ceil(text.length / 160) : 0;
  }

  setCampaignType(type: 'unicast' | 'multicast' | 'excel') {
    this.campaignType.set(type);
    const messageControl = this.campaignForm.get('messageText');
    if (type === 'multicast') {
      messageControl?.clearValidators();
    } else {
      messageControl?.setValidators([Validators.required, Validators.maxLength(1000)]);
    }
    messageControl?.updateValueAndValidity();
    this.resetValidation();
  }

  selectPackage(pkg: string) {
    this.campaignForm.patchValue({ package: pkg });
  }

  selectSenderId(id: string) {
    this.campaignForm.patchValue({ senderId: id });
  }

  onFileSelected(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      this.selectedFile.set(file);
      this.resetValidation();
    }
  }

  resetValidation() {
    this.isValidated.set(false);
    this.campaignCost.set(0);
    this.totalRecipients.set(0);
  }

  downloadSample() {
    const params = {
      type: 'uni'
    };
    if (this.campaignType() === 'multicast') {
      params.type = 'multi';
    } else if (this.campaignType() === 'excel') {
      params.type = 'excel';
    }
    this.campaignService.downloadSampleCampaign(params).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Campaign.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.toasterService.successAlert('Campaign sample downloaded successfully');
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
      }
    });
  }

  validateCampaign() {
    if (this.campaignForm.invalid) {
      this.toasterService.errorAlert('Please fill in all required fields');
      return;
    }

    if (!this.selectedFile()) {
      this.toasterService.errorAlert('Please upload a data file');
      return;
    }

    this.isValidating.set(true);
    const formData = new FormData();
    const formValue = this.campaignForm.getRawValue();

    formData.append('file', this.selectedFile() as File);
    formData.append('package', formValue.package.name);
    let campaignTypeName = 'uni';
    if (this.campaignType() === 'multicast') {
      campaignTypeName = 'multi';
    } else if (this.campaignType() === 'excel') {
      campaignTypeName = 'excel';
    }
    formData.append('type', campaignTypeName);
    if (this.campaignType() !== 'multicast') {
      formData.append('text', formValue.messageText);
    }

    this.campaignService.validateCampaign(formData).subscribe({
      next: (response) => {
        this.isValidated.set(true);
        this.isValidating.set(false);
        this.campaignCost.set(response.data.totalCost || 0);
        this.totalRecipients.set(response.data.totalRecipients || 0);
        this.toasterService.successAlert('Campaign validated successfully');
      },
      error: (error) => {
        this.isValidating.set(false);
        this.toasterService.errorAlert(error.error?.message || 'Validation failed');
      }
    });
  }

  onSubmit() {
    if (!this.isValidated()) {
      this.validateCampaign();
      return;
    }

    const formData = new FormData();
    const formValue = this.campaignForm.getRawValue();

    formData.append('file', this.selectedFile() as File);
    formData.append('campaignName', formValue.name);
    formData.append('package', formValue.package.name);
    formData.append('senderId', formValue.senderId);

    let campaignTypeName = 'uni';
    if (this.campaignType() === 'multicast') {
      campaignTypeName = 'multi';
    } else if (this.campaignType() === 'excel') {
      campaignTypeName = 'excel';
    }
    formData.append('type', campaignTypeName);

    if (this.campaignType() !== 'multicast') {
      formData.append('text', formValue.messageText);
    }

    if (formValue.scheduleDate) {
      formData.append('scheduleDate', formValue.scheduleDate);
    }

    this.campaignService.sendCampaign(formData).subscribe({
      next: () => {
        this.toasterService.successAlert('Campaign launched successfully');
        this.campaignForm.reset();
        this.selectedFile.set(null);
        this.resetValidation();
        this.router.navigate(['/history']);
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error?.message || 'Failed to launch campaign');
      }
    });
  }
}
